import { Timestamp } from "firebase/firestore/lite";
import { DbUser } from "../user/models";
import { DbBusinessWhatsAppHeaderType } from "../whatsapp/models";
import { DbBusinessClient } from "../client/models";

export interface DbBusinessConfig {
  credits: number;
  price: number;
  description?: string;
}

export interface DbBusinessPrivateAlerts {
  credits: number;
  status: boolean;
  allowed_alerts?: string[];
}

export interface DbBusiness {
  id: string;
  name: string;
  category_id: string;
  appointment_phone: string;
  booking_type: DbBusinessBookingType;
  payment_type: DbBusinessPaymentType;
  images: DbBusinessImage[];
  business_state?: DbBusinessState;
  business_hours: any;
  description2: DbBusinessDescription[];
  modules?: DbBusinessModule[];
  business_url?: string;
  address: string;
  locality?: string;
  area?: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  latitude: number;
  longitude: number;
  timezone?: string;
  website?: string;
  email?: string;
  phone?: string;
  visibility: DbBusinessVisibility;
  max_booking_window?: number;
  cancellation_window?: number | null;
  created_by: string;
  created_at?: Timestamp;
  updated_at?: Timestamp;
  subscription?: DbBusinessSubscription;
  has_service?: boolean;
  has_waitlist?: boolean;
  whatsapp_header_type?: DbBusinessWhatsAppHeaderType;
  is_deleted?: boolean;
  remaining_setup_steps?: DbBusinessSetupStep[];
}

export interface DbBusinessSubscription {
  type: DbBusinessSubscriptionType;
  amount: number;
  payment_id?: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
  expiry_date?: Timestamp;
}

export enum DbBusinessSubscriptionType {
  basic_plan = "basic_plan",
  free_trial = "free_trial",
  one_year = "one_year",
  admin = "admin",
  one_year_growth_plan = "one_year_growth_plan",
  free = "free",
}

export interface DbBusinessBillingPurchase {
  id: string;
  paid_amount: number;
  subscription_type: DbBusinessSubscriptionType;
  created_at: Timestamp;
}

export interface DbBusinessReport {
  user_id?: string;
  report_reason: string;
  platform: string;
}

export enum DbBusinessModule {
  Services = "services",
  Waitlist = "waitlist",
  Treatment = "treatment",
  Inventory = "inventory",
}

export enum DbBusinessBookingType {
  Request = "request",
  Confirm = "confirm",
}

export enum DbBusinessPaymentType {
  Advance = "advance",
  Cash = "cash",
  Online = "pg",
  BusinessUPI = "businessUPI",
  RewardPoints = "reward_points",
}

export enum DbBusinessState {
  Created = "created",
  InReview = "in_review",
  Approved = "live",
  Rejected = "rejected",
}

export enum DbWeekDay {
  Sunday = "sun",
  Monday = "mon",
  Tuesday = "tue",
  Wednesday = "wed",
  Thursday = "thu",
  Friday = "fri",
  Saturday = "sat",
}

export interface DbBusinessImage {
  url: string;
  is_primary: boolean;
  blur_hash?: string;
}

export interface DbBusinessDescription {
  title: string;
  description: string;
}

export interface DbBusinessClientPhoto {
  url: string;
  description?: string;
}

export interface DbBusinessWhatsAppPurchase {
  id?: string;
  price: number;
  credits: number;
  description?: string;
  created_at?: Timestamp;
}

export interface DbBusinessReview {
  id: string;
  rating: number;
  review: string;
  is_latest?: boolean;
  service_id: string | null;
  user_id: string;
  calendar_id: string | null;
  created_at: Timestamp;
  user?: DbUser | DbBusinessClient;
  waitlist_id?: string | null;
  visit_id?: string | null;
}

export interface DbBusinessAccountSummary {
  id: string;
  balance: number;
}

export interface DbBusinessBankDetail {
  id: string;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  created_at?: Timestamp;
  updated_at?: Timestamp;
}

export enum DbBusinessVisibility {
  Public = "public",
  Protected = "protected",
  Private = "private",
}

export interface DbBusinessRepeatClients {
  booking_date: Timestamp;
  user_id: string;
}

export enum AlertType {
  BookingConfirmation = "booking_confirmation",
  BookingBeforeTwentyFourHours = "booking_before_24h",
  BookingOnDay = "booking_on_day",
  BookingBeforeOneHour = "booking_before_1h",
  BookingFeedback = "booking_feedback",
  ShareInvoice = "share_invoice",
}

export enum DbBusinessSetupStep {
  AddService = "add_service",
  AddCalendar = "add_calendar",
  AddMembership = "add_membership",
  AddClient = "add_client",
  SendCampaign = "send_campaign",
  MakeFirstBooking = "make_first_booking",
}

export interface DbBusinessSetupChecklistItem {
  id: DbBusinessSetupStep;
  label: string;
  description: string;
  completed: boolean;
}
